import { useEffect, useMemo, useState } from 'react'
import { collection, doc, limit, orderBy, query, serverTimestamp, where } from 'firebase/firestore'
import { deleteDoc, onSnapshot, setDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import type { Capability } from '@/lib/capabilities'
import type { CapabilityGatedKind } from '@/lib/comments'
import { CLEARED_ITEM_IDS, targetIdForKind, urlForKind, type UrlState } from '@/lib/urlState'

/**
 * Activity History — what has happened in the discussions, kept as its own record.
 *
 * The bell is not a history. A notification is gone once it has been dismissed, and until
 * this collection there was nowhere else saying that a reaction had been left or a reply
 * written. That is why a mention could not be cleared: dismissing it lost the only copy of
 * the question. This is the copy, so the bell can go back to being a bell.
 *
 * It is deliberately not the per-item `activity` subcollections, which record field
 * changes — "Status: Open → Done" — on the record they describe. This is the other half:
 * what people said to each other, across every item at once. Two different questions, and
 * a collection group query cannot tell a top-level `activity` from a nested one anyway.
 */
export type ActivityKind = 'comment' | 'reply' | 'mention' | 'reaction' | 'status'

/**
 * What an event can be about.
 *
 * Wider than `CommentParentKind` by one. A gate carries no discussion — there is nothing
 * to reply to and nobody to mention — so it appears here only through its status moving,
 * which is the whole of a gate's story anyway.
 */
// Deliberately not every comment kind: a message thread is read by being on it, so its
// history belongs in the thread and never in a feed keyed on a capability. See
// `CapabilityGatedKind`.
export type ActivityParentKind = CapabilityGatedKind | 'gate'

export type ActivityEvent = {
  id: string
  kind: ActivityKind
  /** Only on a reaction. */
  emoji?: string
  /** Only on a status change: where it moved from and to. */
  from?: string
  to?: string
  /** What the event hangs off, and what gates who may read the row. */
  parentKind: ActivityParentKind
  itemId: string
  itemRef: string
  itemTitle: string
  commentId: string
  body: string
  byEmail: string
  byName: string | null
  /** Who it was aimed at: the person mentioned, or the one being replied to. */
  toName?: string | null
  at: { seconds: number } | null
}

/** The capability that lets somebody see activity about one kind of item. */
const CAPABILITY_FOR: Record<ActivityParentKind, Capability> = {
  decision: 'decisions.view',
  task: 'roadmap.view',
  field: 'fields.view',
  product: 'products.view',
  owner: 'owners.view',
  // A person is a row of the roster wherever the activity was logged from, so this is
  // `staff.view` and not the chart's own grant. Mirrors `kindCapability()` in
  // `firestore.rules` exactly: a listener opened for a kind the reader holds no capability
  // for is a `permission-denied` on first use, not a type error.
  staff: 'staff.view',
  // A lane IS the diagram.
  orgContainer: 'orgchart.view',
  // A note is one of the diagram's side panels — the same grant as the chart it sits on.
  orgNote: 'orgchart.view',
  // A sharing rule is a row on the chart's third sub-tab, and that sub-tab has its own
  // readership — see `match /orgSharingRules` in `firestore.rules`.
  orgSharingRule: 'sharingrule.view',
  // A gate is part of the roadmap, and is read by whoever reads the roadmap.
  gate: 'roadmap.view',
}

export const ACTIVITY_KINDS: ActivityParentKind[] = Object.keys(CAPABILITY_FOR) as ActivityParentKind[]

export const visibleKinds = (can: (c: Capability) => boolean): ActivityParentKind[] =>
  ACTIVITY_KINDS.filter((k) => can(CAPABILITY_FOR[k]))

/**
 * Where a row goes when it is clicked.
 *
 * Everything with a drawer of its own opens it. A gate has none — it is listed on the
 * roadmap alongside the phases it blocks — so a gate row opens that tab and leaves the
 * reader looking at the gates. Every other id is cleared on the way, or a leftover one
 * would put the previous drawer back.
 */
export function urlForActivity(
  e: Pick<ActivityEvent, 'parentKind' | 'itemId' | 'itemRef'>,
): Partial<UrlState> {
  if (e.parentKind === 'gate') {
    return { tab: 'roadmap', ...CLEARED_ITEM_IDS }
  }
  return urlForKind(e.parentKind, targetIdForKind(e.parentKind, e.itemId, e.itemRef))
}

/** An id for an event that only ever happens once, so needs no derived address. */
export const newActivityId = () => doc(collection(db, 'activityEvents')).id

export async function recordActivity(id: string, event: Omit<ActivityEvent, 'id' | 'at'>) {
  await setDoc(doc(db, 'activityEvents', id), { ...event, at: serverTimestamp() })
}

export async function removeActivity(id: string) {
  await deleteDoc(doc(db, 'activityEvents', id))
}

/**
 * The feed, newest first.
 *
 * One listener per kind rather than one query for everything: the rules cannot read a row
 * to decide whether to allow a query, so a query has to name the kind it wants. Asking for
 * everything would be asking for kinds the reader may not have, and would be refused
 * outright rather than filtered.
 *
 * A kind whose listener fails is reported rather than swallowed. Silently showing a
 * shorter history than the one that exists is the failure this whole feature is meant to
 * prevent.
 */
export function useActivityFeed(kinds: ActivityParentKind[], max = 200) {
  const [byKind, setByKind] = useState<Record<string, ActivityEvent[]>>({})
  const [failed, setFailed] = useState<Record<string, string>>({})
  const [settled, setSettled] = useState<Record<string, boolean>>({})
  const key = kinds.join(',')

  useEffect(() => {
    if (key === '') { setByKind({}); setFailed({}); setSettled({}); return }
    const wanted = key.split(',') as ActivityParentKind[]
    const stops = wanted.map((kind) =>
      onSnapshot(
        query(
          collection(db, 'activityEvents'),
          where('parentKind', '==', kind),
          orderBy('at', 'desc'),
          limit(max),
        ),
        (snap) => {
          setByKind((prev) => ({
            ...prev,
            [kind]: snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ActivityEvent),
          }))
          setSettled((prev) => ({ ...prev, [kind]: true }))
        },
        (err) => {
          // The code matters. `failed-precondition` is an index still building and will
          // fix itself; `permission-denied` is a capability and will not. Reporting them
          // the same way sends somebody looking in the wrong place.
          setFailed((prev) => ({ ...prev, [kind]: err.code ?? 'unknown' }))
          setSettled((prev) => ({ ...prev, [kind]: true }))
        },
      ),
    )
    return () => stops.forEach((stop) => stop())
  }, [key, max])

  const events = useMemo(
    () => Object.values(byKind).flat().sort(sortNewestFirst).slice(0, max),
    [byKind, max],
  )

  return {
    events,
    loading: kinds.length > 0 && kinds.some((k) => !settled[k]),
    failedKinds: kinds.filter((k) => failed[k]),
    failureReason: [...new Set(Object.values(failed))].join(', '),
  }
}

/**
 * Newest first, with anything still unstamped at the top.
 *
 * `serverTimestamp()` is null in the local snapshot for the moment between writing and the
 * server answering. That row is the newest thing there is — it is what the reader just
 * did — so it belongs at the top rather than sorted to the bottom as a zero.
 */
const sortNewestFirst = (a: ActivityEvent, b: ActivityEvent) =>
  (b.at?.seconds ?? Number.MAX_SAFE_INTEGER) - (a.at?.seconds ?? Number.MAX_SAFE_INTEGER)
